import {
	faPlus,
	faQuestionCircle,
	faTrash,
	faWandMagicSparkles,
} from '@fortawesome/pro-solid-svg-icons';
import { ConditionalOperator } from '@supply-mover-2-0/core';
import { useState } from 'react';
import { Icon } from '../../../../components/base/Icon';
import { Text } from '../../../../components/base/Text';
import { Card } from '../../../../components/display/Card';
import { Button } from '../../../../components/forms/Button';
import { Input } from '../../../../components/forms/Input';
import { Select } from '../../../../components/forms/Select';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import {
	DataSourceType,
	FieldConfiguration,
	ListBuilderConfiguration,
} from '../../../types';
import { TemplateBuilder } from '../../template-builder/TemplateBuilder';

interface ConditionalEntry {
	field: string;
	operator: ConditionalOperator;
	value: string | string[];
	dataConfig: {
		type: DataSourceType;
		field?: { objectTable: string; fieldName: string };
		customText?: string;
		template?: string;
		templateFields?: Array<{ fieldName: string; objectTable: string }>;
	};
}

interface ConditionalDataConfiguratorProps {
	field: FieldConfiguration;
	configuration?: ListBuilderConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
	availableFields: Array<{
		fieldName: string;
		fieldLabel: string;
		objectTable: string;
		objectLabel: string;
	}>;
}

export function ConditionalDataConfigurator({
	field,
	configuration,
	onUpdateField,
	availableFields,
}: ConditionalDataConfiguratorProps) {
	const colors = useThemeColors();
	const [showTemplateBuilder, setShowTemplateBuilder] = useState<{
		isOpen: boolean;
		type: 'condition' | 'default';
		index?: number;
	}>({ isOpen: false, type: 'condition' });

	// dataConfig is optional on FieldConfiguration; default defensively so a
	// config that omits it can't crash the editor.
	const parentDataConfig = field.dataConfig ?? {
		type: 'conditional' as DataSourceType,
	};
	const conditions = parentDataConfig.conditions || [];
	const defaultDataConfig = parentDataConfig.defaultDataConfig || {
		type: 'field' as DataSourceType,
	};

	const operatorOptions = [
		{ value: 'equals' as ConditionalOperator, label: 'Equals' },
		{ value: 'not_equals' as ConditionalOperator, label: 'Not Equals' },
		{ value: 'in' as ConditionalOperator, label: 'In' },
		{ value: 'not_in' as ConditionalOperator, label: 'Not In' },
	];

	const dataSourceTypeOptions = [
		{ value: 'field' as DataSourceType, label: 'Database Field' },
		{ value: 'custom_text' as DataSourceType, label: 'Custom Text' },
		{ value: 'template' as DataSourceType, label: 'Template' },
	];

	const addCondition = () => {
		const newCondition: ConditionalEntry = {
			field: availableFields[0]?.fieldName || '',
			operator: 'equals',
			value: '',
			dataConfig: { type: 'field' },
		};

		const updatedConditions = [...conditions, newCondition];
		onUpdateField({
			dataConfig: {
				...parentDataConfig,
				conditions: updatedConditions,
			},
		});
	};

	const removeCondition = (index: number) => {
		const updatedConditions = conditions.filter((_, i) => i !== index);
		onUpdateField({
			dataConfig: {
				...parentDataConfig,
				conditions: updatedConditions,
			},
		});
	};

	const updateCondition = (
		index: number,
		updates: Partial<ConditionalEntry>,
	) => {
		const updatedConditions = conditions.map((condition, i) =>
			i === index ? { ...condition, ...updates } : condition,
		);
		onUpdateField({
			dataConfig: {
				...parentDataConfig,
				conditions: updatedConditions,
			},
		});
	};

	const updateDefaultConfig = (updates: any) => {
		onUpdateField({
			dataConfig: {
				...parentDataConfig,
				defaultDataConfig: { ...defaultDataConfig, ...updates },
			},
		});
	};

	const renderDataConfigEditor = (
		dataConfig: any,
		onUpdate: (updates: any) => void,
		isDefault = false,
	) => (
		<div className="space-y-3">
			<Select
				value={dataConfig.type}
				options={dataSourceTypeOptions}
				onChange={(value) =>
					onUpdate({
						type: value,
						field: undefined,
						customText: undefined,
						template: undefined,
					})
				}
				size="sm"
			/>

			{dataConfig.type === 'field' && (
				<Select
					value={
						dataConfig.field
							? `${dataConfig.field.objectTable}:${dataConfig.field.fieldName}`
							: ''
					}
					options={availableFields.map((f) => ({
						value: `${f.objectTable}:${f.fieldName}`,
						label: `${f.objectLabel}: ${f.fieldLabel}`,
					}))}
					onChange={(value) => {
						const [objectTable, fieldName] = value.split(':');
						onUpdate({ field: { objectTable, fieldName } });
					}}
					placeholder="Select field..."
					size="sm"
				/>
			)}

			{dataConfig.type === 'custom_text' && (
				<Input
					value={dataConfig.customText || ''}
					onChange={(e) => onUpdate({ customText: e.target.value })}
					placeholder="Enter custom text..."
					size="sm"
				/>
			)}

			{dataConfig.type === 'template' && (
				<button
					onClick={() =>
						setShowTemplateBuilder({
							isOpen: true,
							type: isDefault ? 'default' : 'condition',
							index: isDefault
								? undefined
								: conditions.findIndex((c) => c.dataConfig === dataConfig),
						})
					}
					className={cn(
						'w-full p-2 border rounded-md transition-colors text-left',
						colors.classes.borderDefault,
						colors.classes.bgSecondary,
						colors.classes.bgPrimaryHover,
					)}
				>
					<div className="flex items-center justify-between">
						<div className="flex-1">
							{dataConfig.template ? (
								<Text className="text-xs line-clamp-1" color="secondary">
									{dataConfig.template}
								</Text>
							) : (
								<Text className="text-xs italic" color="muted">
									Click to configure template...
								</Text>
							)}
						</div>
						<Icon
							icon={faWandMagicSparkles}
							size="xs"
							color={colors.values.primary}
						/>
					</div>
				</button>
			)}
		</div>
	);

	return (
		<div className="space-y-4">
			{/* Conditions */}
			<div>
				<div className="flex items-center justify-between mb-3">
					<Text className="text-sm font-medium">Conditions</Text>
					<Button onClick={addCondition} size="sm" variant="outline">
						<Icon icon={faPlus} size="xs" className="mr-1" />
						Add Condition
					</Button>
				</div>

				<div className="space-y-3">
					{conditions.map((condition, index) => (
						<Card key={index} className="p-3">
							<div className="flex items-center justify-between mb-3">
								<Text className="text-sm font-medium">
									Condition {index + 1}
								</Text>
								<button
									onClick={() => removeCondition(index)}
									className={cn(
										'p-1 rounded hover:bg-danger-tint transition-colors',
										'text-danger hover:text-danger',
									)}
								>
									<Icon icon={faTrash} size="xs" />
								</button>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
								{/* Field Selection */}
								<div>
									<Text className="block text-xs font-medium mb-1" as="label">
										Field to Check
									</Text>
									<Select
										value={condition.field}
										options={availableFields.map((f) => ({
											value: f.fieldName,
											label: f.fieldLabel,
										}))}
										onChange={(value) =>
											updateCondition(index, { field: value })
										}
										size="sm"
									/>
								</div>

								{/* Operator */}
								<div>
									<Text className="block text-xs font-medium mb-1" as="label">
										Operator
									</Text>
									<Select
										value={condition.operator}
										options={operatorOptions}
										onChange={(value) =>
											updateCondition(index, {
												operator: value as ConditionalOperator,
											})
										}
										size="sm"
									/>
								</div>

								{/* Value */}
								<div>
									<Text className="block text-xs font-medium mb-1" as="label">
										Value
									</Text>
									<Input
										value={
											Array.isArray(condition.value)
												? condition.value.join(', ')
												: (condition.value ?? '')
										}
										onChange={(e) => {
											const inputValue = e.target.value;
											const value = ['in', 'not_in'].includes(
												condition.operator,
											)
												? inputValue.split(',').map((v) => v.trim())
												: inputValue;
											updateCondition(index, { value });
										}}
										placeholder={
											['in', 'not_in'].includes(condition.operator)
												? 'value1, value2, value3'
												: 'value'
										}
										size="sm"
									/>
								</div>
							</div>

							{/* Data Config for this condition */}
							<div>
								<Text className="block text-xs font-medium mb-2" as="label">
									Show when condition matches:
								</Text>
								{renderDataConfigEditor(condition.dataConfig, (updates) =>
									updateCondition(index, {
										dataConfig: { ...condition.dataConfig, ...updates },
									}),
								)}
							</div>
						</Card>
					))}
				</div>
			</div>

			{/* Default Configuration */}
			<div>
				<Text className="text-sm font-medium mb-3">
					Default (when no conditions match)
				</Text>
				<Card className="p-3">
					{renderDataConfigEditor(defaultDataConfig, updateDefaultConfig, true)}
				</Card>
			</div>

			{/* Help Text */}
			<div className={cn('p-3 rounded-md', colors.classes.bgSecondary)}>
				<div className="flex items-start">
					<Icon
						icon={faQuestionCircle}
						size="sm"
						color={colors.values.textSecondary}
						className="mr-2 mt-0.5"
					/>
					<div>
						<Text className="text-xs font-medium mb-1">
							How Conditional Data Sources Work
						</Text>
						<Text className="text-xs" color="secondary">
							Conditions are evaluated in order from top to bottom. The first
							matching condition determines what data to display. If no
							conditions match, the default configuration is used.
						</Text>
					</div>
				</div>
			</div>

			{/* Template Builder Modal */}
			{showTemplateBuilder.isOpen && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<div
						className={cn('absolute inset-0', colors.classes.backdropOverlay)}
						onClick={() =>
							setShowTemplateBuilder({ isOpen: false, type: 'condition' })
						}
					/>
					<div
						className={cn(
							'relative w-full max-w-2xl mx-4 rounded-lg shadow-xl',
							colors.classes.bgPrimary,
						)}
					>
						<TemplateBuilder
							value={(() => {
								if (showTemplateBuilder.type === 'default') {
									return defaultDataConfig.template || '';
								}
								if (showTemplateBuilder.index !== undefined) {
									return (
										conditions[showTemplateBuilder.index]?.dataConfig
											.template || ''
									);
								}
								return '';
							})()}
							onChange={(template) => {
								// Extract fields used in template for dependency tracking
								const templateFields = availableFields
									.filter((f) => template.includes(`{${f.fieldName}}`))
									.map((f) => ({
										fieldName: f.fieldName,
										objectTable: f.objectTable,
									}));

								if (showTemplateBuilder.type === 'default') {
									updateDefaultConfig({ template, templateFields });
								} else if (showTemplateBuilder.index !== undefined) {
									const existingCondition =
										conditions[showTemplateBuilder.index];
									if (existingCondition != null) {
										updateCondition(showTemplateBuilder.index, {
											dataConfig: {
												...existingCondition.dataConfig,
												template,
												templateFields,
											},
										});
									}
								}
								setShowTemplateBuilder({ isOpen: false, type: 'condition' });
							}}
							availableFields={availableFields}
							onClose={() =>
								setShowTemplateBuilder({ isOpen: false, type: 'condition' })
							}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
